// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { ProposalType } from '../../../types/proposal';

/** The three proposal types the sectioned builder (`QuoteBackedProposalBuilder`) serves. */
export type QuoteBackedProposalType = 'PENSION' | 'FUND_INVESTMENT' | 'PROTECTION';

export interface QuoteBackedBuilderSectionSeed {
  readonly key: string;
  readonly title: string;
}

export interface QuoteBackedBuilderConfig {
  readonly proposalType: QuoteBackedProposalType;
  /**
   * Prefix used for every `data-testid` the builder rail/shell renders (the
   * shared `pension-builder__*` CSS classes stay literal across all three
   * types — this only disambiguates test hooks per type).
   */
  readonly testIdPrefix: string;
  /**
   * The `…/proposals/{routeSegment}[/:proposalId]` App.tsx route segment for
   * this type's builder. Currently identical to {@link testIdPrefix} by
   * design, but kept as its own field so the two concerns (test hooks vs.
   * routing) can diverge independently later.
   */
  readonly routeSegment: string;
  /** The backend's discriminator key for this type's recommendation section. */
  readonly recommendationSectionKey: string;
  /** Human title of the recommendation section, used in toasts/redirect copy. */
  readonly recommendationSectionTitle: string;
  /**
   * Placeholder titles shown (locked) in "new" mode before the bootstrap has
   * created the proposal + its backend sections. Mirrors the server's
   * default section seed for this type.
   */
  readonly newModeSections: readonly QuoteBackedBuilderSectionSeed[];
}

function newModeSections(
  recommendationKey: string,
  recommendationTitle: string,
): readonly QuoteBackedBuilderSectionSeed[] {
  return [
    { key: 'ABOUT_YOU', title: 'About You' },
    { key: 'NEEDS_OBJECTIVES', title: 'Needs & Objectives' },
    { key: recommendationKey, title: recommendationTitle },
    { key: 'CASHFLOW_IMPACT', title: 'Cashflow Impact' },
    { key: 'SUSTAINABILITY', title: 'Sustainability' },
    { key: 'PRECAUTIONS', title: 'Precautions' },
    { key: 'NEXT_STEPS', title: 'Next Steps' },
    { key: 'DECLARATIONS', title: 'Declarations' },
  ];
}

const PENSION_CONFIG: QuoteBackedBuilderConfig = {
  proposalType: 'PENSION',
  testIdPrefix: 'pension-builder',
  routeSegment: 'pension-builder',
  recommendationSectionKey: 'PENSION_RECOMMENDATION',
  recommendationSectionTitle: 'Pension Recommendation',
  newModeSections: newModeSections('PENSION_RECOMMENDATION', 'Pension Recommendation'),
};

const FUND_INVESTMENT_CONFIG: QuoteBackedBuilderConfig = {
  proposalType: 'FUND_INVESTMENT',
  testIdPrefix: 'investment-builder',
  routeSegment: 'investment-builder',
  recommendationSectionKey: 'INVESTMENT_RECOMMENDATION',
  recommendationSectionTitle: 'Investment Recommendation',
  newModeSections: newModeSections('INVESTMENT_RECOMMENDATION', 'Investment Recommendation'),
};

const PROTECTION_CONFIG: QuoteBackedBuilderConfig = {
  proposalType: 'PROTECTION',
  testIdPrefix: 'protection-builder',
  routeSegment: 'protection-builder',
  recommendationSectionKey: 'PROTECTION_RECOMMENDATION',
  recommendationSectionTitle: 'Protection Recommendation',
  newModeSections: newModeSections('PROTECTION_RECOMMENDATION', 'Protection Recommendation'),
};

const CONFIGS: Record<QuoteBackedProposalType, QuoteBackedBuilderConfig> = {
  PENSION: PENSION_CONFIG,
  FUND_INVESTMENT: FUND_INVESTMENT_CONFIG,
  PROTECTION: PROTECTION_CONFIG,
};

export function getQuoteBackedBuilderConfig(
  proposalType: QuoteBackedProposalType,
): QuoteBackedBuilderConfig {
  return CONFIGS[proposalType];
}

/** Type guard narrowing a generic `ProposalType` to one the builder serves. */
export function isQuoteBackedProposalType(
  proposalType: ProposalType,
): proposalType is QuoteBackedProposalType {
  return proposalType === 'PENSION' || proposalType === 'FUND_INVESTMENT' || proposalType === 'PROTECTION';
}
